import { useState, useEffect, useCallback } from 'react';
import {
  apiService,
  LuckPermsGroupDTO,
  PlayerGroupsResponse,
} from '@/services/api.service';
import styles from './RankManager.module.scss';

interface RankManagerProps {
  playerUuid: string;
  playerName: string;
  onRankChange?: () => void;
}

export function RankManager({ playerUuid, playerName, onRankChange }: RankManagerProps) {
  const [playerGroups, setPlayerGroups] = useState<PlayerGroupsResponse | null>(null);
  const [manageableGroups, setManageableGroups] = useState<LuckPermsGroupDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [groupsResult, manageableResult] = await Promise.all([
      apiService.getPlayerGroups(playerUuid),
      apiService.getManageableGroups(),
    ]);

    if (groupsResult.data) {
      setPlayerGroups(groupsResult.data);
    }

    if (manageableResult.data) {
      setManageableGroups(manageableResult.data.groups);
    }

    setLoading(false);
  }, [playerUuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddGroup = async (groupName: string) => {
    setActionLoading(groupName);
    setError(null);
    setShowAddDropdown(false);

    const result = await apiService.addGroupToPlayer(playerUuid, groupName);

    if (result.error) {
      setError(result.error);
    } else {
      setSuccessMessage(`Grade "${groupName}" ajoute`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadData();
      onRankChange?.();
    }

    setActionLoading(null);
  };

  const handleRemoveGroup = async (groupName: string) => {
    if (!confirm(`Retirer le grade "${groupName}" de ${playerName} ?`)) {
      return;
    }

    setActionLoading(groupName);
    setError(null);

    const result = await apiService.removeGroupFromPlayer(playerUuid, groupName);

    if (result.error) {
      setError(result.error);
    } else {
      setSuccessMessage(`Grade "${groupName}" retire`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadData();
      onRankChange?.();
    }

    setActionLoading(null);
  };

  // Get groups that can be added (manageable but not already assigned)
  const availableToAdd = manageableGroups.filter(
    (g) => !playerGroups?.groups.includes(g.name)
  );

  // Get groups that can be removed (manageable and currently assigned)
  const canRemove = (groupName: string) => {
    if (groupName === 'default') return false;
    return manageableGroups.some((g) => g.name === groupName);
  };

  // If staff can't manage any groups, don't show this component
  if (!loading && manageableGroups.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>
          <span className={styles.cardTitleIcon}>&#x1F451;</span>
          Gestion des Grades
        </h3>
        <div className={styles.loading}>
          <div className={styles.spinnerSmall} />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>
        <span className={styles.cardTitleIcon}>&#x1F451;</span>
        Gestion des Grades
        {availableToAdd.length > 0 && (
          <div className={styles.addButtonContainer}>
            <button
              className={styles.addButton}
              onClick={() => setShowAddDropdown(!showAddDropdown)}
              disabled={actionLoading !== null}
            >
              + Ajouter
            </button>
            {showAddDropdown && (
              <div className={styles.dropdown}>
                {availableToAdd.map((group) => (
                  <button
                    key={group.name}
                    className={styles.dropdownItem}
                    onClick={() => handleAddGroup(group.name)}
                    disabled={actionLoading !== null}
                  >
                    <span className={styles.groupName}>{group.displayName || group.name}</span>
                    <span className={styles.groupId}>{group.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </h3>

      {error && <div className={styles.errorBanner}>{error}</div>}
      {successMessage && <div className={styles.successBanner}>{successMessage}</div>}

      <div className={styles.groupsList}>
        {playerGroups?.groups.map((groupName) => {
          const groupInfo = manageableGroups.find((g) => g.name === groupName);
          const isPrimary = groupName === playerGroups.primaryGroup;
          const isManageable = canRemove(groupName);

          return (
            <div
              key={groupName}
              className={`${styles.groupItem} ${isPrimary ? styles.primary : ''} ${isManageable ? styles.manageable : ''}`}
            >
              <div className={styles.groupInfo}>
                <span className={styles.groupBadge}>
                  {groupInfo?.displayName || groupName}
                </span>
                {isPrimary && <span className={styles.primaryBadge}>Principal</span>}
              </div>
              {isManageable && (
                <button
                  className={styles.removeButton}
                  onClick={() => handleRemoveGroup(groupName)}
                  disabled={actionLoading !== null}
                  title={`Retirer le grade ${groupName}`}
                >
                  {actionLoading === groupName ? (
                    <div className={styles.spinnerTiny} />
                  ) : (
                    <span>&times;</span>
                  )}
                </button>
              )}
            </div>
          );
        })}

        {(!playerGroups?.groups || playerGroups.groups.length === 0) && (
          <p className={styles.emptyText}>Aucun grade</p>
        )}
      </div>

      <div className={styles.hint}>
        Vous pouvez gerer {manageableGroups.length} grade{manageableGroups.length > 1 ? 's' : ''}
      </div>
    </section>
  );
}
